import CssBaseline from "@material-ui/core/CssBaseline"
import { ApolloClient } from "apollo-boost"
import withGA from "next-ga"
import App from "next/app"
import React from "react"
import { Provider } from "react-redux"
import { ApolloProvider, compose } from "react-apollo"
import { hotjar } from "react-hotjar"
import FullPageError from "../components/common/FullPageError"
import { AuthInfoProvider } from "../components/context/AuthContext"
import { EventProvider } from "../components/context/EventContext"
import { NetworkStatusProvider } from "../components/context/NetworkStatusContext"
import { UiProvider } from "../components/context/UiContext"
import withEmptyHoc from "../components/hocs/withEmptyHoc"
import withInitApolloClient from "../components/setup/apollo/withInitApolloClient"
import { Maybe } from "../graphql/generated-types"
import logger from "../server/logger"
import { getWlDomainDataFromRes, storeClientWlDomainData } from "../server/routes/utils"
import { DomainData } from "../server/routes/whitelabeledDomains"
import sentry from "../server/sentry"
import "../styles/style.css"
import { initStore } from "../store/setup"
import { envVars } from "../utils/config"
import { configureRouterProgressBar, importRouter } from "../utils/router"
import MetadataComponentWrapper from "./components/MetadataComponentWrapper"
import Metatags from "./components/Metatags"
import MuiThemeProviderWrapper from "./components/MuiThemeProviderWrapper"

configureRouterProgressBar()
const Router = importRouter()
const { captureException } = sentry({ release: process.env.SENTRY_RELEASE })

interface Props {
    apolloClient: ApolloClient<{}>
    wlDomainData: Maybe<DomainData>
    hasError: boolean
}

interface State {
    hasError: boolean
}

const eventHandler = (event) => {
    // Only run for iOS full screen apps
    // @ts-ignore
    if ("standalone" in window.navigator && window.navigator.standalone) {
        // Only run if link is an anchor and points to the current page
        if (
            event.target.tagName.toLowerCase() !== "a" ||
            event.target.hostname !== window.location.hostname ||
            event.target.pathname !== window.location.pathname ||
            !/#/.test(event.target.href)
        ) {
            return
        }

        // Open link in same tab
        event.preventDefault()
        window.location = event.target.href
    }
}

class MyApp extends App<Props, State> {
    static async getInitialProps({ Component, ctx }) {
        try {
            let pageProps = {}

            if (Component.getInitialProps) {
                pageProps = await Component.getInitialProps(ctx)
            }

            return { pageProps: pageProps || {}, wlDomainData: getWlDomainDataFromRes(ctx.res) }
        } catch (error) {
            // Capture errors that happen during a page's getInitialProps.
            // This will work on both client and server sides.
            const errorEventId = captureException(error, ctx)
            logger.error("Crash in getInitialProps", {
                error: error,
                sentryErrorEventId: errorEventId,
            })
            return {
                hasError: true,
                errorEventId: errorEventId,
                pageProps: {},
            }
        }
    }

    state = {
        hasError: false,
    }

    componentDidCatch(error, errorInfo) {
        // @ts-ignore
        const errorEventId = captureException(error, { errorInfo: errorInfo })
        logger.error("Crash in componentDidCatch", {
            error: error,
            sentryErrorEventId: errorEventId,
        })
    }

    static getDerivedStateFromError() {
        return { hasError: true }
    }

    shouldShowErrorPage = () => this.props.hasError || this.state.hasError

    componentDidMount() {
        // Remove the server-side injected CSS.
        const jssStyles = document.querySelector("#jss-server-side")
        if (jssStyles) {
            jssStyles.parentNode.removeChild(jssStyles)
        }

        window.addEventListener("click", eventHandler, false)

        // Init HotJar
        if (envVars.HOTJAR_ID) {
            setTimeout(() => hotjar.initialize(envVars.HOTJAR_ID, 6), 0) // defer
        }

        // Store whitelabeled domain data for client-side use
        if (this.props.wlDomainData) {
            storeClientWlDomainData(this.props.wlDomainData)
        }
    }

    render() {
        const { Component, pageProps, apolloClient } = this.props

        if (this.shouldShowErrorPage()) {
            return (
                <Provider store={initStore}>
                    <Metatags />
                    <MuiThemeProviderWrapper displayPreloading={true} isThemeWrapper={true}>
                        <CssBaseline />
                        <FullPageError />
                    </MuiThemeProviderWrapper>
                </Provider>
            )
        }

        return (
            <div>
                <Provider store={initStore}>
                    <Metatags />
                    {!apolloClient ? (
                        <MuiThemeProviderWrapper displayPreloading={true} isThemeWrapper={true}>
                            <CssBaseline />
                            <Component {...pageProps} displayPreloading={true} />
                        </MuiThemeProviderWrapper>
                    ) : (
                        <ApolloProvider client={apolloClient}>
                            <EventProvider>
                                <NetworkStatusProvider>
                                    <UiProvider>
                                        <AuthInfoProvider>
                                            <MuiThemeProviderWrapper isThemeWrapper={true}>
                                                <MetadataComponentWrapper />
                                                <CssBaseline />
                                                <Component {...pageProps} />
                                            </MuiThemeProviderWrapper>
                                        </AuthInfoProvider>
                                    </UiProvider>
                                </NetworkStatusProvider>
                            </EventProvider>
                        </ApolloProvider>
                    )}
                </Provider>
            </div>
        )
    }
}

const composed = compose(
    withInitApolloClient,
    envVars.GOOGLE_ANALYTICS_ID ? withGA(envVars.GOOGLE_ANALYTICS_ID, Router) : withEmptyHoc
)

export default composed(MyApp)
